import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  startWith,
} from 'rxjs';
import { RmAction, RmActionName } from '../models/rm-actions.model';
import { RmNavGroup } from '../models/company-permission.model';
import { ContextService } from './context.service';
import {
  getRmListTabs,
  getRepairNavGroup,
  sortRmListTabsForDesign,
  sortRmNavSubMenusForDesign,
} from '../utils/rm-list-tab.util';
import { usesRmBePermissions } from '../utils/rm-action-permission.util';

/**
 * UI usage map for BE RepairAndMaintenance actionPermissions keys.
 */
export const RM_ACTION_USAGE: Record<RmActionName, readonly string[]> = {
  [RmAction.Create]: [
    'list.createNew',
    'form.createRequest',
    'route.new',
    'sidebar.create',
  ],
  [RmAction.View]: [
    'list.viewRequest',
    'route.list',
    'route.view',
    'sidebar.repairMaintenance',
  ],
  [RmAction.UpdateDraft]: [
    'form.saveDraft',
    'list.editRequest.draftOrReturned',
    'route.edit.draft',
    'list.deleteDraft',
  ],
  [RmAction.DeleteDraft]: ['list.deleteDraft'],
  [RmAction.Withdraw]: ['list.withdraw', 'detail.withdraw'],
  [RmAction.Submit]: [
    'form.submit',
    'form.updateRequest',
    'list.editRequest.pending',
    'route.edit.pending',
  ],
  [RmAction.Update]: ['form.updateRequest', 'list.editRequest'],
  [RmAction.Comment]: ['detail.comment'],
  [RmAction.Return]: ['detail.return', 'list.return'],
  [RmAction.Reject]: ['detail.reject', 'list.reject'],
  [RmAction.Approve]: ['detail.approve', 'list.approve'],
  [RmAction.ReferToCompanyAdmin]: ['detail.referToCompanyAdmin'],
  [RmAction.AssignVendor]: ['detail.assignVendor', 'detail.inviteVendor'],
  [RmAction.InviteVendor]: ['detail.inviteVendor', 'detail.assignVendor'],
  [RmAction.ExternalInviteVendor]: ['detail.inviteExternalVendor'],
  [RmAction.VendorInviteRevoke]: ['detail.revokeVendorInvite'],
  [RmAction.StartWork]: ['detail.startWork', 'detail.workStarted'],
  [RmAction.CompleteWork]: ['detail.completeWork', 'detail.workCompleted'],
  [RmAction.AddQuotation]: ['detail.addQuotation'],
  [RmAction.EditQuotation]: ['detail.editQuotation'],
  [RmAction.ApproveQuotation]: ['detail.approveQuotation'],
  [RmAction.RejectQuotation]: ['detail.rejectQuotation'],
  [RmAction.QuotationItemApproval]: ['detail.approveQuotationItem'],
  [RmAction.QuotationItemRejection]: ['detail.rejectQuotationItem'],
  [RmAction.SubmitQuotation]: ['detail.submitQuotationInvitedVendor'],
  [RmAction.SubmitInvoice]: ['detail.submitInvoice'],
  [RmAction.EditInvoice]: ['detail.editInvoice'],
  [RmAction.ApproveInvoice]: ['detail.approveInvoice'],
  [RmAction.RejectInvoice]: ['detail.rejectInvoice'],
  [RmAction.InvoiceItemApproval]: ['detail.approveInvoiceItem'],
  [RmAction.InvoiceItemRejection]: ['detail.rejectInvoiceItem'],
  [RmAction.IntegrateToAP]: ['detail.integrateToAp'],
  [RmAction.CreateApprovalProcess]: [
    'list.createApprovalProcess',
    'approvalProcess.create',
  ],
  [RmAction.UpdateApprovalProcess]: [
    'list.updateApprovalProcess',
    'approvalProcess.edit',
  ],
  [RmAction.DeleteApprovalProcess]: [
    'list.deleteApprovalProcess',
    'approvalProcess.delete',
  ],
};

type RmPermissionSnapshot = {
  roleId: string | null;
  companyId: string | null;
  usesBePermissions: boolean;
  actions: ReadonlyMap<string, boolean>;
  navGroup: RmNavGroup | null;
};

@Injectable({
  providedIn: 'root',
})
export class RmPermissionService {
  private readonly snapshot$ = new BehaviorSubject<RmPermissionSnapshot>(
    this.buildSnapshot()
  );

  readonly actions$ = this.snapshot$.pipe(map((s) => s.actions));
  readonly navGroup$ = this.snapshot$.pipe(map((s) => s.navGroup));
  readonly usesBePermissions$ = this.snapshot$.pipe(
    map((s) => s.usesBePermissions)
  );

  constructor(private readonly contextSvc: ContextService) {
    combineLatest([
      this.contextSvc.selectedRoleId$.pipe(
        startWith(this.contextSvc.selectedRoleId$.value)
      ),
      this.contextSvc.selectedCompanyId$.pipe(
        startWith(this.contextSvc.getCompanyId())
      ),
      this.contextSvc.user.pipe(startWith(this.contextSvc.user.value)),
      this.contextSvc.onApRolePermissionsChanged$.pipe(startWith(null)),
    ])
      .pipe(
        map(() => this.buildSnapshot()),
        distinctUntilChanged(
          (a, b) =>
            a.roleId === b.roleId &&
            a.companyId === b.companyId &&
            a.usesBePermissions === b.usesBePermissions &&
            this.sameActionMap(a.actions, b.actions) &&
            JSON.stringify(a.navGroup) === JSON.stringify(b.navGroup)
        )
      )
      .subscribe((snapshot) => this.snapshot$.next(snapshot));
  }

  can(action: RmActionName | string, legacyAllowed = false): boolean {
    const snapshot = this.snapshot$.value;
    if (snapshot.actions.get(action) === true) {
      return true;
    }

    return this.contextSvc.isRmActionAllowed(action, legacyAllowed);
  }

  canAny(
    actions: ReadonlyArray<RmActionName | string>,
    legacyAllowed = false
  ): boolean {
    return actions.some((action) => this.can(action, legacyAllowed));
  }

  usesBePermissions(): boolean {
    return this.snapshot$.value.usesBePermissions;
  }

  getNavGroup(): RmNavGroup | null {
    const group = this.snapshot$.value.navGroup;
    if (!group?.subMenus?.length) {
      return group;
    }
    return {
      ...group,
      subMenus: sortRmNavSubMenusForDesign(
        group.subMenus,
        this.snapshot$.value.roleId
      ),
    };
  }

  getRmListTabs() {
    return sortRmListTabsForDesign(
      getRmListTabs(
        this.contextSvc.getStoredPermissionsForRole(
          this.snapshot$.value.roleId
        ),
        this.snapshot$.value.companyId
      ),
      this.snapshot$.value.roleId
    );
  }

  hasSidebar(): boolean {
    return Boolean(this.getNavGroup()?.subMenus?.length);
  }

  canCreate(): boolean {
    return this.can(RmAction.Create);
  }

  canView(): boolean {
    return this.can(RmAction.View) || this.hasSidebar();
  }

  canEditRoute(): boolean {
    return this.canAny([
      RmAction.UpdateDraft,
      RmAction.Submit,
      RmAction.Update,
    ]);
  }

  refresh(): void {
    this.snapshot$.next(this.buildSnapshot());
  }

  private buildSnapshot(): RmPermissionSnapshot {
    const roleId =
      this.contextSvc.selectedRoleId$.value ??
      (typeof localStorage !== 'undefined'
        ? localStorage.getItem('selectedRoleId')
        : null);
    const companyId = this.contextSvc.getActiveCompanyId();
    const stored = this.contextSvc.getStoredPermissionsForRole(roleId);

    const actions = new Map<string, boolean>();
    (Object.values(RmAction) as string[]).forEach((action) => {
      actions.set(action, this.contextSvc.isRmActionAllowed(action, false));
    });

    return {
      roleId,
      companyId,
      usesBePermissions: usesRmBePermissions(stored, companyId),
      actions,
      navGroup: getRepairNavGroup(stored, companyId, roleId),
    };
  }

  private sameActionMap(
    a: ReadonlyMap<string, boolean>,
    b: ReadonlyMap<string, boolean>
  ): boolean {
    if (a.size !== b.size) {
      return false;
    }
    for (const [key, value] of a) {
      if (b.get(key) !== value) {
        return false;
      }
    }
    return true;
  }
}
